import { UltimateGuitarParser, type Line, type Song } from 'chordsheetjs';
import { preNormalize, type PreNormalizeResult } from './preNormalize.ts';
import { isContentEmpty, segment, type SegmentResult } from './segment.ts';
import {
  buildAnchor,
  buildSignature,
  dedupeAdjacent,
  findSmallestPeriod,
  inheritProgressions,
  normalizeChords,
  rawChordPhrases,
} from './progression.ts';
import type {
  Chart,
  ChartMeta,
  ChartSection,
  ChartVariant,
  DegradationLevel,
  Diagnostic,
  Progression,
  SongId,
} from './types.ts';

// Kroki (E), (F) i złożenie końcowe potoku: mapa układu (zamrożona przed
// jakimkolwiek scalaniem), diagnostyki po polsku, i wreszcie `compile()` oraz
// `compileVariants()`, które spinają preNormalize → parser ChordSheetJS →
// segment → progression w jeden `Chart` i jego sześć wariantów degradacji.

/**
 * Buduje mapę układu utworu z pełnej listy sekcji, PRZED jakimkolwiek
 * scalaniem czy degradacją. Ten sam string trafia do każdego z ośmiu
 * `ChartVariant`, bo mapa opisuje przebieg utworu, nie to, co akurat widać.
 *
 * Podział na bloki oddzielone " | ": nowy blok zaczyna się przed sekcją typu
 * zwrotka albo intro, JEŻELI w bieżącym bloku wystąpił już refren. To NIE jest
 * to samo co "po refrenie": w `kiler.txt` druga zwrotka następuje po
 * instrumentalu, nie po refrenie, a mimo to zaczyna nowy blok, bo refren w tym
 * bloku już był wcześniej.
 */
export function layoutMap(sections: readonly ChartSection[]): string {
  const blocks: string[][] = [[]];
  let sawChorusInBlock = false;

  for (const section of sections) {
    const startsNewBlock = (section.kind === 'verse' || section.kind === 'intro') && sawChorusInBlock;
    if (startsNewBlock) {
      blocks.push([]);
      sawChorusInBlock = false;
    }
    blocks[blocks.length - 1]!.push(section.labels.join('/'));
    if (section.kind === 'chorus') sawChorusInBlock = true;
  }

  return blocks.map((block) => block.join(' ')).join(' | ');
}

/** Wyciąga tekst (lyrics) pojedynczej linii, konkatenując fragmenty ze
 * wszystkich par akord-tekst tej linii, w kolejności. */
function lineLyrics(line: Line): string {
  let out = '';
  for (const item of line.items) {
    if ('lyrics' in item && typeof item.lyrics === 'string') out += item.lyrics;
  }
  return out;
}

/** Sklejone `lyrics` całej sekcji, jeden wielowierszowy string, wejście do `buildAnchor`. */
function sectionLyrics(lines: readonly Line[]): string {
  return lines.map(lineLyrics).join('\n');
}

function firstOf(value: string | readonly string[] | null): string | null {
  if (value === null) return null;
  if (Array.isArray(value)) return (value[0] as string | undefined) ?? null;
  return value as string;
}

function extractLeadingNumber(value: string | null): number | null {
  if (value === null) return null;
  const match = value.match(/\d+/);
  return match ? Number.parseInt(match[0], 10) : null;
}

/** Buduje `ChartMeta` z metadanych rozpoznanych przez ChordSheetJS oraz z
 * `Bicie:`/`Rytm:`/`Strumming:` wyciętego wcześniej przez `preNormalize`.
 * `notes` (uwagi końcowe autora) pochodzą z `segment()`, poza zakresem
 * `preNorm`, więc są osobnym argumentem. */
function buildMeta(song: Song, preNorm: PreNormalizeResult, notes: string | null): ChartMeta {
  // Formalne "Capo: N" ma zawsze pierwszeństwo. Dopiero gdy go nie ma, sięgamy po
  // sygnał z nieformalnego zdania w preambule (np. "CAPO ON 2ND FRET" w
  // shape-of-my-heart.txt), patrz `preNormalize.ts`. Gdy zdanie wspomina kapodaster
  // bez numeru progu, `informalCapo.fret` jest `null` i zostaje takie tu też -
  // NIE zgadujemy, `diagnose()` podnosi za to `CAPO_MENTION_NO_FRET`.
  const formalCapo = extractLeadingNumber(firstOf(song.capo));
  return {
    key: firstOf(song.key),
    capo: formalCapo ?? preNorm.informalCapo?.fret ?? null,
    time: firstOf(song.time),
    tempo: extractLeadingNumber(firstOf(song.tempo)),
    strumming: preNorm.strumming,
    notes,
  };
}

const EMPTY_META: ChartMeta = {
  key: null, capo: null, time: null, tempo: null, strumming: null, notes: null,
};

function errorChart(songId: SongId, meta: ChartMeta, diagnostic: Diagnostic): Chart {
  return {
    songId,
    meta,
    sections: [],
    layoutMap: '',
    diagnostics: [diagnostic],
    usable: false,
  };
}

interface DiagnoseParams {
  rawTab: string;
  preNorm: PreNormalizeResult;
  segmentResult: SegmentResult;
  sections: readonly ChartSection[];
  inheritedFlags: readonly boolean[];
  unparsedChordTokens: readonly string[];
  /** Finalny (już rozstrzygnięty formalny/nieformalny) kapodaster, do diagnostyki. */
  resolvedCapo: number | null;
  /** Id sekcji z prawdziwym nagłówkiem, które są puste (ani akordów, ani tekstu),
   * a NIE są `tabOnly` (bo w ich obrębie nie wycięto ani jednej linii tabulatury). */
  emptyNonTabSectionIds: readonly string[];
}

// Kilka różnych zapisów "xN"/"Nx" w CAŁYM utworze (niezależnie od tego, czy
// leżą w tabulaturze, w nagłówku, czy w linii akordów): każdy pojedynczy
// zapis jest jednoznaczny, ale gdy jest ich więcej niż jeden w tym samym
// pliku, zakres, którego dotyczy dany zapis, nie zawsze wynika z samego taba
// (realny przypadek: nie-placz-ewka.txt ma cztery takie notacje, z których
// część dotyczy pojedynczego wzoru arpeggia, nie całej sekcji). Sprawdzone na
// całym korpusie: pliki z co najwyżej JEDNYM takim zapisem (np. "2x" w
// niepokonani.txt i kiler.txt) nie są niejednoznaczne.
const REPEAT_NOTATION_RE = /(?<![\p{L}\d])(\d+x|x\d+)(?![\p{L}])/giu;
const NUMERIC_DEGREE_RE = /^[1-7]$/;
const ROMAN_DEGREE_RE = /^(i{1,3}|iv|v|vi{1,2})$/i;

function diagnose(params: DiagnoseParams): Diagnostic[] {
  const {
    rawTab, preNorm, segmentResult, sections, inheritedFlags, unparsedChordTokens,
    resolvedCapo, emptyNonTabSectionIds,
  } = params;
  const diagnostics: Diagnostic[] = [];

  for (const hit of preNorm.headers) {
    if (hit.isTypo) {
      diagnostics.push({
        code: 'HEADER_TYPO',
        severity: 'info',
        message: `Nagłówek "${hit.original}" rozpoznany przez tolerancję literówek jako "${hit.label}".`,
      });
    }
  }

  const totalChords = sections.reduce((sum, s) => sum + s.progression.raw.length, 0);
  if (preNorm.tabLinesRemoved > 0 && totalChords === 0) {
    diagnostics.push({
      code: 'TAB_SHEET',
      severity: 'error',
      message: 'Po wycięciu tabulatury nutowej nie zostaje ani jeden akord. '
        + 'Wybierz na Ultimate Guitar wersję "Chords", nie "Tabs".',
    });
  } else if (preNorm.tabLinesRemoved > 0) {
    diagnostics.push({
      code: 'TAB_MIXED',
      severity: 'warn',
      message: `Wycięto ${preNorm.tabLinesRemoved} linii tabulatury nutowej wplecionej między akordami.`,
    });
  } else if (totalChords === 0) {
    diagnostics.push({
      code: 'NO_CHORDS',
      severity: 'error',
      message: 'W tekście nie ma ani jednego akordu. Wklej wersję "Chords", nie "Lyrics".',
    });
  }

  if (preNorm.headers.length === 0) {
    diagnostics.push({
      code: 'NO_SECTION_HEADERS',
      severity: 'warn',
      message: 'Brak rozpoznanych nagłówków sekcji, utwór podzielono po pustych liniach.',
    });
  }

  if (sections.length === 1) {
    diagnostics.push({
      code: 'SINGLE_SECTION',
      severity: 'info',
      message: 'Utwór ma tylko jedną sekcję.',
    });
  }

  const uniqueSignatures = new Set(sections.map((s) => s.signature));
  if (uniqueSignatures.size > 8) {
    diagnostics.push({
      code: 'TOO_MANY_SECTIONS',
      severity: 'warn',
      message: 'Ponad osiem sekcji po scaleniu, część informacji zostanie ukryta na najniższych '
        + 'poziomach degradacji.',
    });
  }

  sections.forEach((s, i) => {
    if (inheritedFlags[i]) {
      diagnostics.push({
        code: 'INHERITED_PROGRESSION',
        severity: 'info',
        message: `Sekcja "${s.labels.join('/')}" nie ma własnych akordów, dziedziczy progresję `
          + 'po wcześniejszej sekcji tego samego rodzaju.',
        sectionId: s.id,
      });
    }
    if (s.tabOnly) {
      diagnostics.push({
        code: 'TAB_ONLY_SECTION',
        severity: 'info',
        message: `Sekcja "${s.labels.join('/')}" jest zapisana wyłącznie tabulaturą, `
          + 'bez ani jednej nazwy akordu.',
        sectionId: s.id,
      });
    }
  });

  for (const dropped of segmentResult.droppedEmpty) {
    diagnostics.push({
      code: 'EMPTY_SECTION',
      severity: 'info',
      message: `Pominięto pustą sekcję "${dropped.label}", bo po wycięciach nie miała `
        + 'ani akordów, ani tekstu.',
    });
  }

  // Sekcja z PRAWDZIWYM nagłówkiem, pusta (ani akordów, ani tekstu), ale w JEJ
  // obrębie nie wycięto ani jednej linii tabulatury: to NIE jest `TAB_ONLY_SECTION`
  // (nie ma dowodu, że treścią było zapisanie tabulaturowe), tylko zwykła pusta
  // sekcja, ta sama diagnostyka co dla sekcji odrzuconych z komentarza wyżej.
  for (const s of sections) {
    if (!emptyNonTabSectionIds.includes(s.id)) continue;
    diagnostics.push({
      code: 'EMPTY_SECTION',
      severity: 'info',
      message: `Sekcja "${s.labels.join('/')}" nie ma ani akordów, ani tekstu, a w jej `
        + 'obrębie nie wycięto tabulatury, więc nie jest to zapis tabulaturowy.',
      sectionId: s.id,
    });
  }

  const hasNumericDegree = unparsedChordTokens.some((token) => {
    const trimmed = token.trim();
    return NUMERIC_DEGREE_RE.test(trimmed) || ROMAN_DEGREE_RE.test(trimmed);
  });
  if (hasNumericDegree) {
    diagnostics.push({
      code: 'NUMERIC_CHORDS',
      severity: 'info',
      message: 'Wygląda na zapis stopniowy (numeryczny albo rzymski), podaj tonację, '
        + 'żeby zamienić na nazwy akordów.',
    });
  }

  const repeatMatches = rawTab.match(REPEAT_NOTATION_RE) ?? [];
  if (repeatMatches.length > 1) {
    diagnostics.push({
      code: 'AMBIGUOUS_REPEAT',
      severity: 'info',
      message: `W utworze jest kilka różnych zapisów powtórzenia (${repeatMatches.join(', ')}), `
        + 'zakres każdego nie zawsze wynika wprost z taba.',
    });
  }

  for (const dropped of segmentResult.droppedInstructional) {
    diagnostics.push({
      code: 'INSTRUCTIONAL_BLOCK',
      severity: 'info',
      message: `Sekcja "${dropped.label}" rozpoznana jako blok instruktażowy `
        + '(pokaz wzoru gry, bez ani jednego słowa tekstu) i pominięta w przebiegu utworu.',
    });
  }

  if (preNorm.informalCapo !== null && preNorm.informalCapo.fret === null && resolvedCapo === null) {
    diagnostics.push({
      code: 'CAPO_MENTION_NO_FRET',
      severity: 'warn',
      message: 'Tab wspomina o kapodastrze zdaniem w preambule, ale nie podaje numeru progu, '
        + 'kapodaster zostaje nieustalony.',
    });
  }

  return diagnostics;
}

/**
 * Pełny potok: preNormalize → `UltimateGuitarParser().parse()` → segment →
 * progression → `Chart`. Nie scala i nie okraja sekcji: `Chart.sections`
 * zostaje w kolejności oryginalnej. Nigdy nie rzuca na niepoprawnym wejściu,
 * zamiast tego zwraca `Chart` z `usable: false` i diagnostyką błędu.
 *
 * `songId` jest argumentem, bo `Chart.songId` nie ma skąd się wziąć z samego
 * tekstu taba.
 */
export function compile(songId: SongId, rawTab: string): Chart {
  if (rawTab.trim() === '') {
    return errorChart(songId, EMPTY_META, {
      code: 'EMPTY_INPUT',
      severity: 'error',
      message: 'Wklejony tekst jest pusty.',
    });
  }

  const preNorm = preNormalize(rawTab);

  let song: Song;
  try {
    song = new UltimateGuitarParser().parse(preNorm.text);
  } catch (err) {
    return errorChart(songId, { ...EMPTY_META, strumming: preNorm.strumming }, {
      code: 'UNRECOGNIZED_HEADER',
      severity: 'error',
      message: `Nie udało się rozpoznać struktury utworu: ${(err as Error).message}`,
    });
  }

  let segmentResult: SegmentResult;
  try {
    segmentResult = segment(song, preNorm.headers);
  } catch (err) {
    return errorChart(songId, buildMeta(song, preNorm, null), {
      code: 'UNRECOGNIZED_HEADER',
      severity: 'error',
      message: `Błąd segmentacji: ${(err as Error).message}`,
    });
  }

  const rawSections = segmentResult.sections;
  // Frazowanie: akordy pogrupowane po linii źródłowej (patrz `rawChordPhrases`),
  // normalizowane fraza po frazie, żeby `Progression.phrases` niosło już
  // ZNORMALIZOWANE tokeny (te same, które trafiają do `raw`/`base`). `raw`
  // pozostaje płaskim spłaszczeniem tych fraz - to jest cały niezmiennik.
  const rawPhrasesList = rawSections.map((s) => rawChordPhrases(s.lines));
  const normalizedPhrasesList: string[][][] = [];
  const unparsedChordTokensList: string[][] = [];
  for (const phrases of rawPhrasesList) {
    const normalizedPhrases: string[][] = [];
    const unparsed: string[] = [];
    for (const phrase of phrases) {
      const { tokens, unparsedChordTokens } = normalizeChords(phrase);
      normalizedPhrases.push(tokens);
      unparsed.push(...unparsedChordTokens);
    }
    normalizedPhrasesList.push(normalizedPhrases);
    unparsedChordTokensList.push(unparsed);
  }
  const flatTokensList = normalizedPhrasesList.map((phrases) => phrases.flat());
  const progressions: Progression[] = rawSections.map((s, i) => {
    const core = findSmallestPeriod(flatTokensList[i]!, s.times);
    return { ...core, phrases: normalizedPhrasesList[i]! };
  });
  const hasOwnChordsList = flatTokensList.map((tokens) => tokens.length > 0);

  const inheritanceResults = inheritProgressions(
    rawSections.map((s, i) => ({
      kind: s.kind,
      progression: progressions[i]!,
      hasOwnChords: hasOwnChordsList[i]!,
    })),
  );

  const emptyNonTabSectionIds: string[] = [];

  const sections: ChartSection[] = rawSections.map((s, i) => {
    const progression = inheritanceResults[i]!.progression;
    const inherited = inheritanceResults[i]!.inherited;
    // Sekcja bez zwykłej treści (ani akordów, ani tekstu) MIMO że `segment()`
    // jej nie odrzucił (bo zaczęła się prawdziwym nagłówkiem): MOŻE być w
    // praktyce sekcją zapisaną wyłącznie tabulaturą, którą `preNormalize` już
    // wyciął, ale TYLKO gdy w jej obrębie faktycznie wycięto choć jedną linię
    // tabulatury (`s.hadTabLinesRemoved`) - inaczej to zwykła pusta sekcja
    // (audyt: bez tego warunku `tabOnly` fałszywie twierdziło "partia
    // tabulaturowa" tam, gdzie w źródle nie było ani śladu tabulatury).
    const contentEmpty = progression.raw.length === 0 && !inherited && isContentEmpty(s.lines);
    const tabOnly = contentEmpty && s.hadTabLinesRemoved;
    if (contentEmpty && !tabOnly) emptyNonTabSectionIds.push(`s${i}`);
    const signature = buildSignature(s.kind, progression);
    const anchor = buildAnchor(sectionLyrics(s.lines));
    const isInstrumental = s.kind === 'instrumental' || s.kind === 'solo' || s.kind === 'interlude';

    return {
      id: `s${i}`,
      kind: s.kind,
      labels: [s.label],
      mergedCount: 1,
      progression,
      anchor,
      signature,
      isInstrumental,
      tabOnly,
      description: s.description,
    };
  });

  const meta = buildMeta(song, preNorm, segmentResult.closingNotes);
  const unparsedChordTokens = unparsedChordTokensList.flat();
  const map = layoutMap(sections);
  const diagnostics = diagnose({
    rawTab,
    preNorm,
    segmentResult,
    sections,
    inheritedFlags: inheritanceResults.map((r) => r.inherited),
    unparsedChordTokens,
    resolvedCapo: meta.capo,
    emptyNonTabSectionIds,
  });

  return {
    songId,
    meta,
    sections,
    layoutMap: map,
    diagnostics,
    usable: !diagnostics.some((d) => d.severity === 'error'),
  };
}

/**
 * Rozwija `Chart` w listę OŚMIU wariantów degradacji (poziomy 0-7), każdy
 * nadzbiorem cięć poprzedniego. Zwraca zawsze wszystkie osiem naraz (nie
 * strumień na żądanie), bo `fitLoop` mierzy poziomy liniowo i potrzebuje
 * dostępu swobodnego do każdego z nich.
 *
 * `layoutMap` jest tym samym stringiem (tą samą referencją z `Chart`) we
 * wszystkich ośmiu wariantach, bo mapa opisuje przebieg utworu, nie to,
 * co akurat widać po degradacji.
 *
 * KOLEJNOŚĆ DRABINY (poprawiona po audycie e2e): frazowanie degraduje
 * WCZEŚNIEJ niż kotwice (poziomy 2-3 przed 4-5), bo przy graniu kotwica jest
 * ważniejsza niż podział na frazy - poprzednia kolejność (frazowanie na
 * poziomie 4, PO kotwicach na 2-3) płaciła kotwicami za frazy, których i tak
 * nie było już widać, bo do poziomu 4 dochodziło się dopiero po ich utracie.
 * Dane sekcji (`Progression.phrases`) NIE zmieniają się na żadnym poziomie:
 * poziomy 2 i 3 są czysto PREZENTACYJNE, ich jedynym efektem jest
 * `ChartVariant.phrasingMode`, czytany przez `src/render/chart.ts`.
 */
export function compileVariants(chart: Chart): readonly ChartVariant[] {
  const base = chart.sections;
  const hadPhrasing = base.some((s) => s.progression.phrases.length > 1);

  const level0: ChartVariant = {
    level: 0,
    meta: chart.meta,
    sections: base,
    layoutMap: chart.layoutMap,
    droppedInfo: [],
    phrasingMode: 'lines',
    references: EMPTY_REFERENCES,
  };

  // Poziom 1: bicie i tempo znikają z meta. Sekcje i frazowanie bez zmian.
  const level1Meta: ChartMeta = { ...chart.meta, strumming: null, tempo: null };
  const level1DroppedInfo: string[] = [];
  if (chart.meta.strumming !== null) level1DroppedInfo.push('bicie');
  if (chart.meta.tempo !== null) level1DroppedInfo.push('tempo');
  const level1: ChartVariant = {
    level: 1,
    meta: level1Meta,
    sections: base,
    layoutMap: chart.layoutMap,
    droppedInfo: level1DroppedInfo,
    phrasingMode: 'lines',
    references: EMPTY_REFERENCES,
  };

  // Poziom 2: frazy schodzą z osobnych wierszy na SEPARATORY w jednym ciągu.
  // Dane sekcji bez zmian, to czysto prezentacyjne przestawienie trybu.
  const level2DroppedInfo = [...level1DroppedInfo];
  if (hadPhrasing) level2DroppedInfo.push('frazy w osobnych wierszach');
  const level2: ChartVariant = {
    level: 2,
    meta: level1Meta,
    sections: base,
    layoutMap: chart.layoutMap,
    droppedInfo: level2DroppedInfo,
    phrasingMode: 'separators',
    references: EMPTY_REFERENCES,
  };

  // Poziom 3: frazy całkiem płaskie, bez żadnego znaku granicy (sam
  // spłaszczony ciąg `base ×times + tail`). Dane sekcji nadal bez zmian.
  const level3DroppedInfo = [...level2DroppedInfo];
  if (hadPhrasing) level3DroppedInfo.push('znaki podziału fraz');
  const level3: ChartVariant = {
    level: 3,
    meta: level1Meta,
    sections: base,
    layoutMap: chart.layoutMap,
    droppedInfo: level3DroppedInfo,
    phrasingMode: 'flat',
    references: EMPTY_REFERENCES,
  };

  // Poziom 4: kotwice znikają w sekcjach, których sygnatura (kind + raw,
  // zamrożona na poziomie 0) powtarza się gdziekolwiek w utworze.
  const signatureCounts = new Map<string, number>();
  for (const s of base) signatureCounts.set(s.signature, (signatureCounts.get(s.signature) ?? 0) + 1);

  const level4Sections: ChartSection[] = base.map((s) => (
    (signatureCounts.get(s.signature) ?? 0) > 1 ? { ...s, anchor: null } : s
  ));
  const level4AnchorsDropped = level4Sections.some((s, i) => s.anchor === null && base[i]!.anchor !== null);
  const level4DroppedInfo = [...level3DroppedInfo];
  if (level4AnchorsDropped) level4DroppedInfo.push('kotwice w powtarzających się sekcjach');
  const level4: ChartVariant = {
    level: 4,
    meta: level1Meta,
    sections: level4Sections,
    layoutMap: chart.layoutMap,
    droppedInfo: level4DroppedInfo,
    phrasingMode: 'flat',
    references: EMPTY_REFERENCES,
  };

  // Poziom 5: kotwice znikają wszędzie poza pierwszą sekcją.
  const level5Sections: ChartSection[] = level4Sections.map((s, i) => (
    i === 0 || s.anchor === null ? s : { ...s, anchor: null }
  ));
  const level5AnchorsDropped = level5Sections.some(
    (s, i) => s.anchor === null && level4Sections[i]!.anchor !== null,
  );
  const level5DroppedInfo = [...level4DroppedInfo];
  if (level5AnchorsDropped) level5DroppedInfo.push('pozostałe kotwice');
  const level5: ChartVariant = {
    level: 5,
    meta: level1Meta,
    sections: level5Sections,
    layoutMap: chart.layoutMap,
    droppedInfo: level5DroppedInfo,
    phrasingMode: 'flat',
    references: EMPTY_REFERENCES,
  };

  // Poziom 6: zwinięcie sąsiadujących powtórzeń akordu w każdej sekcji.
  // `signature` NIE jest przeliczana, zostaje z poziomu 0, dokładnie po to,
  // żeby ochrona przed scaleniem (poziom 7) nie zniknęła po zwinięciu.
  const level6Sections: ChartSection[] = level5Sections.map((s) => {
    const deduped = dedupeAdjacent(s.progression.raw);
    if (deduped.length === s.progression.raw.length) return s;
    const newProgression: Progression = {
      raw: deduped,
      base: deduped,
      times: 1,
      tail: [],
      timesFromSource: false,
      phrases: [deduped],
    };
    return { ...s, progression: newProgression };
  });
  const level6Changed = level6Sections.some((s, i) => s.progression !== level5Sections[i]!.progression);
  const level6DroppedInfo = [...level5DroppedInfo];
  if (level6Changed) level6DroppedInfo.push('powtórzenia akordów w sekcji');
  const level6: ChartVariant = {
    level: 6,
    meta: level1Meta,
    sections: level6Sections,
    layoutMap: chart.layoutMap,
    droppedInfo: level6DroppedInfo,
    phrasingMode: 'flat',
    references: EMPTY_REFERENCES,
  };

  // Poziom 7a: odsyłacz "jak Z1" MIĘDZY RODZAJAMI, liczony na `level6Sections`
  // (a więc PRZED scaleniem), w kolejności oryginalnej. Patrz `buildReferences`
  // niżej dla regułę i uzasadnienie każdego warunku.
  const rawReferences = buildReferences(level6Sections);

  // Poziom 7b: scalenie sekcji o identycznej sygnaturze TEGO SAMEGO TYPU.
  // Sygnatura już niesie `kind`, więc scalanie MIĘDZY typami jest niemożliwe
  // z definicji: refren o akordach zwrotki ma inną sygnaturę niż zwrotka.
  const seenSignatures = new Set<string>();
  const level7Sections: ChartSection[] = [];
  for (const s of level6Sections) {
    if (seenSignatures.has(s.signature)) continue;
    seenSignatures.add(s.signature);
    const group = level6Sections.filter((x) => x.signature === s.signature);
    level7Sections.push(group.length === 1 ? s : {
      ...s,
      labels: group.flatMap((g) => g.labels),
      mergedCount: group.reduce((sum, g) => sum + g.mergedCount, 0),
    });
  }
  // Wiersz scalony zachowuje `id` PIERWSZEGO elementu swojej grupy (patrz pętla
  // wyżej), a `buildReferences` liczyła po `level6Sections`, czyli PRZED
  // scaleniem, więc mapowanie po tym samym `id` po prostu działa: wszyscy
  // członkowie grupy mają tę samą progresję i ten sam rodzaj (bo scalanie
  // grupuje po `signature` = rodzaj + progresja), więc mają ten sam odsyłacz
  // albo żaden - sprawdzone testem w `compile.test.ts`.
  const level7References: Record<string, string> = {};
  for (const s of level7Sections) {
    const target = rawReferences.get(s.id);
    if (target !== undefined) level7References[s.id] = target;
  }
  const level7DroppedInfo = [...level6DroppedInfo];
  if (level7Sections.length < level6Sections.length) level7DroppedInfo.push('scalone sekcje o identycznych akordach');
  if (Object.keys(level7References).length > 0) level7DroppedInfo.push('akordy sekcji pokazane odsyłaczem');
  const level7: ChartVariant = {
    level: 7,
    meta: level1Meta,
    sections: level7Sections,
    layoutMap: chart.layoutMap,
    droppedInfo: level7DroppedInfo,
    phrasingMode: 'flat',
    references: level7References,
  };

  return [level0, level1, level2, level3, level4, level5, level6, level7];
}

/** Puste `references` dzielone przez wszystkie warianty poziomów 0-6, żeby nie
 * alokować nowego pustego obiektu osiem razy dla każdego skompilowanego utworu. */
const EMPTY_REFERENCES: Readonly<Record<string, string>> = {};

/**
 * Minimalna długość progresji (`Progression.raw`), od której odsyłacz
 * `jak Z1` w ogóle się opłaca. Dobrana ARBITRALNIE, wolno ją zmienić: przy
 * dwóch albo trzech akordach napis `jak Z1` bywa dłuższy od samej progresji,
 * którą miał zastąpić.
 */
const MIN_AKORDOW_DO_ODSYLACZA = 4;

/**
 * Porównanie progresji PO ELEMENTACH, nie po `join(' ')`: nazwa akordu nie
 * zawiera spacji, więc `join` dziś by wystarczył, ale nie ma powodu opierać
 * na tym poprawności.
 */
function sameProgression(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Odsyłacz „jak Z1" zamiast przedruku identycznej progresji, wyłącznie
 * MIĘDZY RODZAJAMI (patrz uzasadnienie wąskiego zakresu w specyfikacji
 * `spec-odsylacz.md`, sekcja "Po co to jest i dlaczego akurat tak wąsko").
 * Liczona na `sections` w kolejności oryginalnej (tu zawsze `level6Sections`,
 * PRZED scaleniem poziomu 7).
 *
 * Sekcja docelowa jest zawsze PIERWSZA w swojej klasie progresji (bo szukamy
 * pierwszego, a nie najbliższego dopasowania), więc sama nigdy nie ma
 * odsyłacza - łańcuchy „A jak B, B jak C" są niemożliwe z konstrukcji, nie z
 * dodatkowego sprawdzenia.
 */
function buildReferences(sections: readonly ChartSection[]): Map<string, string> {
  const references = new Map<string, string>();
  for (let i = 0; i < sections.length; i += 1) {
    const s = sections[i]!;
    if (s.tabOnly || s.progression.raw.length === 0) continue;
    if (s.progression.raw.length < MIN_AKORDOW_DO_ODSYLACZA) continue;

    let target: ChartSection | undefined;
    for (let j = 0; j < i; j += 1) {
      if (sameProgression(sections[j]!.progression.raw, s.progression.raw)) {
        target = sections[j]!;
        break;
      }
    }
    if (target === undefined || target.tabOnly || target.kind === s.kind) continue;

    references.set(s.id, target.labels[0]!);
  }
  return references;
}

/** Poziomy degradacji w kolejności rosnącej agresywności cięć, dla iteracji. */
export const DEGRADATION_LEVELS: readonly DegradationLevel[] = [0, 1, 2, 3, 4, 5, 6, 7];
